const focusableSelector = [
  "input:not([type='hidden'])",
  "select",
  "textarea",
  "button",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

const getBracketNotation = (path) =>
  path.replace(/\.([^.[\]]+)/g, "[$1]");

export const getErrorPaths = (errors) => {
  const paths = [];

  const walk = (value, prefix = "") => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const path = `${prefix}[${index}]`;
        if (item && typeof item === "object") {
          walk(item, path);
        } else if (item) {
          paths.push(path);
        }
      });
      return;
    }

    if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, item]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (item && typeof item === "object") {
          walk(item, path);
        } else if (item) {
          paths.push(path);
        }
      });
      return;
    }

    if (value && prefix) paths.push(prefix);
  };

  walk(errors);
  return paths;
};

const isElementVisible = (element) => {
  if (!element || element.closest(".side-panel")) return false;

  const style = window.getComputedStyle(element);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    element.type === "hidden" ||
    element.offsetParent === null
  ) {
    return false;
  }

  return element.getClientRects().length > 0;
};

const getVisibleTarget = (element) => {
  if (!element || element.closest(".side-panel")) return null;
  if (isElementVisible(element)) return element;

  const relatedTargets = [
    element.closest("[data-error-path]"),
    element.parentElement?.closest(".is-invalid"),
    element.closest(".form-group"),
    element.closest(".imagesUpload"),
    element.closest(".card-body"),
  ];

  return relatedTargets.find((target) => isElementVisible(target)) || null;
};

const matchesErrorPath = (targetPaths, errorPath) =>
  targetPaths.split(",").some((targetPath) => {
    const normalizedPath = targetPath.trim();
    return (
      normalizedPath === errorPath ||
      errorPath.startsWith(`${normalizedPath}[`) ||
      normalizedPath.startsWith(`${errorPath}[`) ||
      errorPath.startsWith(`${normalizedPath}.`) ||
      normalizedPath.startsWith(`${errorPath}.`)
    );
  });

const getCandidates = (errorPath) => {
  const names = [errorPath, getBracketNotation(errorPath)];
  const namedElements = names.reduce(
    (elements, name) => elements.concat(Array.from(document.getElementsByName(name))),
    [],
  );
  const explicitTargets = Array.from(
    document.querySelectorAll("[data-error-path]"),
  ).filter((element) =>
    matchesErrorPath(element.dataset.errorPath || "", errorPath),
  );

  return [...explicitTargets, ...namedElements];
};

const focusTarget = (target) => {
  const focusable = target.matches(focusableSelector)
    ? target
    : target.querySelector(focusableSelector);

  if (focusable) {
    focusable.focus({ preventScroll: true });
  }
};

export const scrollToFirstError = (errorPaths) => {
  for (const errorPath of errorPaths) {
    const target = getCandidates(errorPath)
      .map(getVisibleTarget)
      .find(Boolean);

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      focusTarget(target);
      return errorPath;
    }
  }

  return null;
};

export const scrollToFirstInvalidTarget = () => {
  const target = Array.from(document.querySelectorAll(".is-invalid"))
    .map(getVisibleTarget)
    .find(Boolean);

  if (!target) return false;

  target.scrollIntoView({ behavior: "smooth", block: "center" });
  focusTarget(target);
  return true;
};
